import { ColumnDef } from "@tanstack/table-core";
import { useFlags } from "flagsmith/react";
import { useCallback, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { CustomColumnsTable } from "src/components/Table/CustomColumnsTable";
import { SortType } from "src/components/Table/SortButton";
import {
  FILTER_DELIMITER,
  FormTabsTypes,
  SearchAndFilterTypes,
} from "src/constants";
import { useDefaultColumnsProps } from "src/hooks/useDefaultColumnsProps";
import { useGlossary } from "src/hooks/useGlossary";
import { useIsOfferEnabled } from "src/hooks/useIsOfferEnabled";
import { Selection } from "src/hooks/useMultiselectState";
import { useOrganization } from "src/hooks/useOrganization";
import { checkboxColumnDef as buildCheckboxColumnDef } from "src/hooks/useTableSelection";
import * as GQL from "src/types/graphql";
import { AdvancedViewSettings } from "./advancedViewSettings/AdvancedViewSettings";
import { TableBanner } from "./components/TableBanner";
import { TableHeader } from "./components/TableHeader";
import { MenuItem } from "./components/menuItem";
import {
  buildColumnsForTab,
  buildVisibilityColumnDef,
  getDefaultColumnIdsForTab,
  hasAdditionalColumns,
} from "./formTables/columns";
import { ColumnId } from "./formTables/constants";
import type {
  FormIdListResult,
  FormListRow,
  SchoolFormKeyRecord,
} from "./types";

interface FormsListProps {
  enrollmentPeriodId: uuid;
  forms: FormListRow[];
  formTemplate: GQL.FormTemplateFragment;
  isLoading?: boolean;
  onSort: (columnName: string, sortType: SortType) => void;
  offset: number;
  limit: number;
  count: number;
  selection: Selection<SchoolFormKeyRecord, FormListRow>;
  onSelectionChange: (
    selection: Selection<SchoolFormKeyRecord, FormListRow>
  ) => void;
  onRefetch: () => Promise<unknown>;
  onFetchAllIds: () => Promise<FormIdListResult>;
  onFetchMore: (limit: number, offset: number) => void;
  onSelectAll: (count: number) => void;
  tabType: FormTabsTypes;
  setAttendance: (data: string) => void;
}

export const FormsList: React.FC<FormsListProps> = ({
  enrollmentPeriodId,
  forms,
  formTemplate,
  isLoading,
  onSort,
  offset,
  limit,
  count,
  selection,
  onSelectionChange,
  onRefetch,
  onFetchAllIds,
  onFetchMore,
  onSelectAll,
  tabType,
  setAttendance,
}) => {
  const { glossary } = useGlossary();
  const organization = useOrganization();
  const flags = useFlags([
    "saved-view-custom-columns",
    "current-applying-schools",
  ]);
  const isCustomColumnsEnabled = flags["saved-view-custom-columns"].enabled;
  const isCurrentApplyingSchoolsEnabled =
    flags["current-applying-schools"].enabled;

  const hasGradesQuestion =
    formTemplate.sections
      .flatMap((x) => x.questions)
      .find((x) => x.type === "Grades") !== undefined;
  const hasSchoolRankingSection = formTemplate.sections.find(
    (x) => x.type === "SchoolRankingSection"
  );

  const isOfferEnabled = useIsOfferEnabled(organization, formTemplate);

  useEffect(() => {
    if (tabType === FormTabsTypes.FormListImports) {
      onSelectAll(count);
    }
    // only want to perform selectAll when currentTab and count changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabType, count]);

  const defaultColumnsProps = useDefaultColumnsProps({
    formTemplate,
  });

  const [searchParams, setSearchParams] = useSearchParams();
  const additionalColumnsIncluded = useMemo(() => {
    return hasAdditionalColumns(searchParams, defaultColumnsProps);
  }, [searchParams, defaultColumnsProps]);

  const columns: ColumnDef<FormListRow>[] = useMemo(() => {
    return [
      buildCheckboxColumnDef({
        id: "Selection",
        count,
        onSelectionChange,
        selection,
      }),
      buildVisibilityColumnDef(),
      ...buildColumnsForTab({
        tabType,
        additionalColumnsIncluded,
        glossary,
        organization,
        formTemplate,
        isCurrentApplyingSchoolsEnabled,
        ...defaultColumnsProps,
      }),
    ];
  }, [
    additionalColumnsIncluded,
    count,
    defaultColumnsProps,
    formTemplate,
    glossary,
    isCurrentApplyingSchoolsEnabled,
    onSelectionChange,
    organization,
    selection,
    tabType,
  ]);

  const handleChangeSort = useCallback(
    (headerId: string, sortType: SortType) => {
      onSort(headerId, sortType);
    },
    [onSort]
  );

  const handleClearSelection = useCallback(() => {
    onSelectionChange(selection.clear());
  }, [onSelectionChange, selection]);

  const sortableColumnIds = [
    ColumnId.WaitlistPosition,
    ColumnId.StudentFirstName,
    ColumnId.StudentLastName,
    ColumnId.SchoolRank,
    ColumnId.Status,
    ColumnId.StatusDate,
    ColumnId.SubStatus,
    ColumnId.SubStatusDate,
    ColumnId.Tiebreaker,
  ];

  if (hasGradesQuestion) sortableColumnIds.push(ColumnId.Grade);
  if (hasSchoolRankingSection) sortableColumnIds.push(ColumnId.School);

  return (
    <CustomColumnsTable<FormListRow>
      tableHeader={({ count, more }) => (
        <TableHeader count={count} setAttendance={setAttendance} more={more} />
      )}
      data={forms}
      columns={columns}
      showAdvancedViewSettingsOnly={additionalColumnsIncluded}
      requiredColumnIds={["Selection", ColumnId.Visibility]}
      resourceName={`form-list-${tabType}-${formTemplate.id}`}
      isLoading={isLoading}
      sortableColumnIds={sortableColumnIds}
      onChangeSort={handleChangeSort}
      count={count}
      offset={offset}
      limit={limit}
      onFetchMore={onFetchMore}
      banner={() => (
        <TableBanner
          hiddenMenuItems={[...(isOfferEnabled ? [] : [MenuItem.Offers])]}
          formTemplate={formTemplate}
          enrollmentPeriodId={enrollmentPeriodId}
          includeUpdateStatus={
            tabType !== FormTabsTypes.Waitlists &&
            tabType !== FormTabsTypes.Offers
          }
          onFetchAllIds={onFetchAllIds}
          onRefetch={onRefetch}
          selection={selection}
          onClearSelection={handleClearSelection}
        />
      )}
      advancedViewSettings={
        isCustomColumnsEnabled && (
          <AdvancedViewSettings
            formTemplate={formTemplate}
            showResetToDefaultColumns={additionalColumnsIncluded}
            onResetToDefaultColumns={() => {
              const newSearchParams = new URLSearchParams(searchParams);
              newSearchParams.set(
                SearchAndFilterTypes.VisibleColumns,
                getDefaultColumnIdsForTab({
                  tabType,
                  ...defaultColumnsProps,
                }).join(FILTER_DELIMITER)
              );
              setSearchParams(newSearchParams, { replace: true });
            }}
          />
        )
      }
      columnVisibilityPrefixesToExpand={["question-"]}
    />
  );
};
